import { useState } from "react";
import {
  Box,
  Button,
  Heading,
  Input,
  Text,
  FormControl,
  VStack,
  SimpleGrid,
  Container,
  Grid,
  GridItem,
  useColorMode,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { emailReg, getCurrentColor } from "../../constants";
import useHttp from "../../hooks/useHttp";
import SuccessFormSend from "../SuccessFormSend/SuccessFormSend";
import ErrorFormSend from "../ErrorFormSend/ErrorFormSend";

const ShotShowFormSection = () => {
  const { colorMode } = useColorMode();
  const { postVendorForm } = useHttp();
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm();

  const onSubmit = (values) => {
    setIsLoading(true);
    postVendorForm({
      business_name: values.company,
      contact_name: values.name,
      email_address: values.email,
      role: values.role,
      shot_show_form: true,
    })
      .then((res) => {
        setIsLoading(false);
        if (
          res.code === "ERR_NETWORK" ||
          res.code === "ERR_BAD_REQUEST" ||
          res.code === "ERR_BAD_RESPONSE"
        ) {
          setIsError(true);
        } else if (res.status === 200) {
          setIsSuccessful(true);
        }
      })
      .catch(() => {
        setIsLoading(false);
        setIsError(true);
      });
  };

  return (
    <Box
      bg={getCurrentColor(colorMode, "whiteC.200", "blueC.500")}
      py={{ base: "64px", md: "80px" }}
      borderTop="1px solid"
      borderColor={getCurrentColor(colorMode, "grayC.500", "grayC.2000")}
      id="partner-form"
    >
      <Container maxW="7xl">
        <Grid
          templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
          gap={{ base: 8, lg: 12 }}
        >
          {/* Left column - SHOT Show content */}
          <GridItem>
            <Heading
              as="h2"
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="700"
              color={getCurrentColor(colorMode, "blackC.100", "whiteC.200")}
              mb={6}
              lineHeight="tight"
            >
              SHOT Show Drives Interest.
            </Heading>
            <Heading
              as="h3"
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="600"
              color="blueC.100"
              mb={6}
              lineHeight="tight"
            >
              BulletScout Drives Traffic.
            </Heading>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color={getCurrentColor(colorMode, "grayC.300", "grayC.700")}
              lineHeight="relaxed"
            >
              SHOT Show creates interest, conversations, and intent—but the
              buying journey continues afterward. When shooters begin
              researching prices and availability, BulletScout is where many of
              those searches happen, connecting demand to retailers at the
              moment it matters most.
            </Text>
          </GridItem>

          {/* Right column - Partner CTA form */}
          <GridItem>
            <Box
              bg={getCurrentColor(colorMode, "blueC.800", "grayC.1700")}
              borderRadius="lg"
              p={{ base: 6, md: 12 }}
              border="1px solid"
              borderColor={getCurrentColor(
                colorMode,
                "grayC.500",
                "grayC.2000"
              )}
            >
              {isSuccessful && !isError ? (
                <SuccessFormSend setSuccess={setIsSuccessful} />
              ) : isError && !isSuccessful ? (
                <ErrorFormSend setError={setIsError} />
              ) : (
                <>
                  <Heading
                    as="h3"
                    fontSize={{ base: "xl", md: "3xl" }}
                    fontWeight="700"
                    color={getCurrentColor(
                      colorMode,
                      "blackC.100",
                      "whiteC.200"
                    )}
                    mb={4}
                    lineHeight="tight"
                  >
                    Be Where Ammo Searches Start
                  </Heading>
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    color={getCurrentColor(colorMode, "grayC.300", "grayC.700")}
                    mb={8}
                    lineHeight="relaxed"
                  >
                    If you're a retailer, manufacturer, or industry partner
                    interested in visibility, traffic, or market insight, we'd
                    love to connect.
                  </Text>

                  <form onSubmit={handleSubmit(onSubmit)}>
                    <VStack spacing={4}>
                      <SimpleGrid
                        columns={{ base: 1, md: 2 }}
                        spacing={4}
                        w="100%"
                      >
                        <FormControl isInvalid={errors.name}>
                          <Input
                            placeholder="Your Name"
                            bg={getCurrentColor(
                              colorMode,
                              "whiteC.200",
                              "blueC.500"
                            )}
                            color={getCurrentColor(
                              colorMode,
                              "blackC.100",
                              "whiteC.200"
                            )}
                            border="1px solid"
                            borderColor={getCurrentColor(
                              colorMode,
                              "grayC.500",
                              "grayC.2000"
                            )}
                            borderRadius="md"
                            px={4}
                            py={6}
                            _placeholder={{
                              color: getCurrentColor(
                                colorMode,
                                "grayC.300",
                                "grayC.700"
                              ),
                            }}
                            _focus={{
                              borderColor: "blueC.100",
                              boxShadow: "none",
                            }}
                            _hover={{ borderColor: "blueC.100" }}
                            {...register("name", {
                              required: "Name is required",
                            })}
                          />
                        </FormControl>
                        <FormControl isInvalid={errors.company}>
                          <Input
                            placeholder="Company"
                            bg={getCurrentColor(
                              colorMode,
                              "whiteC.200",
                              "blueC.500"
                            )}
                            color={getCurrentColor(
                              colorMode,
                              "blackC.100",
                              "whiteC.200"
                            )}
                            border="1px solid"
                            borderColor={getCurrentColor(
                              colorMode,
                              "grayC.500",
                              "grayC.2000"
                            )}
                            borderRadius="md"
                            px={4}
                            py={6}
                            _placeholder={{
                              color: getCurrentColor(
                                colorMode,
                                "grayC.300",
                                "grayC.700"
                              ),
                            }}
                            _focus={{
                              borderColor: "blueC.100",
                              boxShadow: "none",
                            }}
                            _hover={{ borderColor: "blueC.100" }}
                            {...register("company", {
                              required: "Company is required",
                            })}
                          />
                        </FormControl>
                      </SimpleGrid>
                      <SimpleGrid
                        columns={{ base: 1, md: 2 }}
                        spacing={4}
                        w="100%"
                      >
                        <FormControl isInvalid={errors.email}>
                          <Input
                            type="email"
                            placeholder="Email"
                            bg={getCurrentColor(
                              colorMode,
                              "whiteC.200",
                              "blueC.500"
                            )}
                            color={getCurrentColor(
                              colorMode,
                              "blackC.100",
                              "whiteC.200"
                            )}
                            border="1px solid"
                            borderColor={getCurrentColor(
                              colorMode,
                              "grayC.500",
                              "grayC.2000"
                            )}
                            borderRadius="md"
                            px={4}
                            py={6}
                            _placeholder={{
                              color: getCurrentColor(
                                colorMode,
                                "grayC.300",
                                "grayC.700"
                              ),
                            }}
                            _focus={{
                              borderColor: "blueC.100",
                              boxShadow: "none",
                            }}
                            _hover={{ borderColor: "blueC.100" }}
                            {...register("email", {
                              required: "Email is required",
                              pattern: {
                                value: emailReg,
                                message: "Invalid email",
                              },
                            })}
                          />
                        </FormControl>
                        <FormControl isInvalid={errors.role}>
                          <Input
                            placeholder="Your Role"
                            bg={getCurrentColor(
                              colorMode,
                              "whiteC.200",
                              "blueC.500"
                            )}
                            color={getCurrentColor(
                              colorMode,
                              "blackC.100",
                              "whiteC.200"
                            )}
                            border="1px solid"
                            borderColor={getCurrentColor(
                              colorMode,
                              "grayC.500",
                              "grayC.2000"
                            )}
                            borderRadius="md"
                            px={4}
                            py={6}
                            _placeholder={{
                              color: getCurrentColor(
                                colorMode,
                                "grayC.300",
                                "grayC.700"
                              ),
                            }}
                            _focus={{
                              borderColor: "blueC.100",
                              boxShadow: "none",
                            }}
                            _hover={{ borderColor: "blueC.100" }}
                            {...register("role", {
                              required: "Role is required",
                            })}
                          />
                        </FormControl>
                      </SimpleGrid>
                      <Button
                        type="submit"
                        w="100%"
                        bg={"blueC.100"}
                        color={"whiteC.100"}
                        px={6}
                        py={6}
                        borderRadius="md"
                        fontWeight="700"
                        fontSize="lg"
                        isLoading={isLoading}
                        _hover={{ opacity: 0.9 }}
                        transition="all 0.2s"
                      >
                        Let's Talk After SHOT Show
                      </Button>
                    </VStack>
                  </form>
                </>
              )}
            </Box>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
};

export default ShotShowFormSection;